import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Layout } from '../components/Layout';
import { Plus, PencilSimple, Trash, X, CalendarBlank } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DIAS = ['Segunda', 'Terca', 'Quarta', 'Quinta', 'Sexta', 'Sabado', 'Domingo'];
const EMPTY = { dia: 'Segunda', horario: '18:30', turma: '', kind: 'fixa' };

function TurmaForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  return (
    <div className="bg-white border-2 border-[#E85D04] rounded-xl p-4 space-y-3" data-testid="turma-form">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <label className="text-sm">
          <span className="block text-xs font-semibold text-[#73706A] mb-1">Dia</span>
          <select value={form.dia} onChange={e => set('dia', e.target.value)} className="w-full border border-[#E6E2D8] rounded px-2 py-2 text-sm" data-testid="turma-dia">
            {DIAS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-xs font-semibold text-[#73706A] mb-1">Horário</span>
          <input type="time" value={form.horario} onChange={e => set('horario', e.target.value)} className="w-full border border-[#E6E2D8] rounded px-2 py-2 text-sm" data-testid="turma-horario" />
        </label>
        <label className="text-sm sm:col-span-2">
          <span className="block text-xs font-semibold text-[#73706A] mb-1">Nome da turma</span>
          <input value={form.turma} onChange={e => set('turma', e.target.value)} placeholder="Ex: Feminino Avancado" className="w-full border border-[#E6E2D8] rounded px-2 py-2 text-sm" data-testid="turma-nome" />
        </label>
      </div>
      <label className="text-sm block">
        <span className="block text-xs font-semibold text-[#73706A] mb-1">Categoria</span>
        <select value={form.kind} onChange={e => set('kind', e.target.value)} className="w-full sm:w-64 border border-[#E6E2D8] rounded px-2 py-2 text-sm" data-testid="turma-kind">
          <option value="fixa">Futevôlei</option>
          <option value="funcional">Treinamento Funcional</option>
        </select>
      </label>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="text-sm text-[#73706A] px-3 py-1.5 hover:text-[#1A1A1A]" data-testid="turma-cancel">Cancelar</button>
        <button onClick={() => form.turma.trim() ? onSave(form) : toast.error('Informe o nome da turma')} disabled={saving} className="text-sm bg-[#E85D04] text-white px-4 py-1.5 rounded-lg hover:bg-[#DC2F02] disabled:opacity-50 font-semibold" data-testid="turma-save">
          {saving ? 'Salvando...' : 'Salvar'}
        </button>
      </div>
    </div>
  );
}

export default function TurmasManage() {
  const [turmas, setTurmas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const token = () => localStorage.getItem('token');
  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/turmas`, { headers: { Authorization: `Bearer ${token()}` } });
      setTurmas(data);
    } catch { toast.error('Erro ao carregar turmas'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (form) => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/turmas`, form, { headers: { Authorization: `Bearer ${token()}` } });
      toast.success('Turma criada'); setAdding(false); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); } finally { setSaving(false); }
  };
  const update = async (id, form) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/turmas/${id}`, form, { headers: { Authorization: `Bearer ${token()}` } });
      toast.success('Turma atualizada'); setEditingId(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); } finally { setSaving(false); }
  };
  const remove = async (id, nome) => {
    if (!window.confirm(`Remover a turma "${nome}"?`)) return;
    try {
      await axios.delete(`${API}/admin/turmas/${id}`, { headers: { Authorization: `Bearer ${token()}` } });
      toast.success('Turma removida'); await load();
    } catch { toast.error('Erro ao remover'); }
  };

  const fut = turmas.filter(t => t.kind !== 'funcional');
  const func = turmas.filter(t => t.kind === 'funcional');

  const Section = ({ title, items, color }) => (
    <section className="space-y-2">
      <h2 className="font-['Outfit'] font-bold text-[#1A1A1A]" style={{ color }}>{title}</h2>
      {items.length === 0 && <p className="text-sm text-[#73706A]">Nenhuma turma.</p>}
      {items.map(t => editingId === t.id ? (
        <TurmaForm key={t.id} initial={{ dia: t.dia, horario: t.horario, turma: t.turma, kind: t.kind }} onSave={(f) => update(t.id, f)} onCancel={() => setEditingId(null)} saving={saving} />
      ) : (
        <div key={t.id} className="flex items-center gap-3 bg-white border border-[#E6E2D8] rounded-lg px-4 py-3" data-testid={`turma-row-${t.id}`}>
          <div className="w-24 flex-shrink-0">
            <p className="text-sm font-bold text-[#1A1A1A]">{t.dia}</p>
            <p className="text-xs text-[#73706A]">{t.horario}</p>
          </div>
          <p className="flex-1 text-sm text-[#1A1A1A] font-medium">{t.turma}</p>
          <button onClick={() => { setEditingId(t.id); setAdding(false); }} className="text-[#0077B6] hover:bg-blue-50 p-2 rounded" data-testid={`turma-edit-${t.id}`}><PencilSimple size={18} /></button>
          <button onClick={() => remove(t.id, t.turma)} className="text-[#9B2226] hover:bg-red-50 p-2 rounded" data-testid={`turma-delete-${t.id}`}><Trash size={18} /></button>
        </div>
      ))}
    </section>
  );

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-6" data-testid="turmas-manage-page">
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-3">
            <CalendarBlank size={28} className="text-[#E85D04]" weight="fill" />
            <h1 className="font-['Outfit'] text-2xl font-bold text-[#1A1A1A]">Turmas e Horários</h1>
          </div>
          <button onClick={() => { setAdding(a => !a); setEditingId(null); }} className="inline-flex items-center gap-2 bg-[#E85D04] text-white px-4 py-2 rounded-lg font-semibold text-sm hover:bg-[#DC2F02]" data-testid="add-turma-btn">
            {adding ? <X size={18} /> : <Plus size={18} weight="bold" />} {adding ? 'Fechar' : 'Nova turma'}
          </button>
        </div>
        <p className="text-sm text-[#73706A] mb-4">Adicione, edite ou remova turmas e seus horários. As mudanças aparecem na tela de cadastro e no portal do aluno.</p>
        {adding && <div className="mb-5"><TurmaForm initial={EMPTY} onSave={create} onCancel={() => setAdding(false)} saving={saving} /></div>}
        {loading ? <p className="text-center text-[#73706A] py-8">Carregando...</p> : (
          <div className="space-y-8">
            <Section title="Futevôlei" items={fut} color="#E85D04" />
            <Section title="Treinamento Funcional" items={func} color="#0077B6" />
          </div>
        )}
      </div>
    </Layout>
  );
}
